import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { PlayCircle, PauseCircle } from "react-bootstrap-icons"
import { useDispatch, useSelector } from "react-redux"
import { prependAndPlay, playOrPause } from "../../../stores/mediaPlayerSlice"
import {
  useUserPlaylists,
  useFollowedPlaylists,
} from "../../../hooks/useUserPlaylists"
import { usePost } from "../../../hooks/usePost"
import { sortedSongs } from "../../posts/PlaylistListItem"
import { PLAYLIST_PLACEHOLDER_IMAGE_URL } from "../../../util/constants"
import Heading from "../../shared/Heading"

interface UserPlaylistsProps {
  userId: number
}

interface PlaylistRowProps {
  playlist: any
}

const PlaylistRow: React.FC<PlaylistRowProps> = ({ playlist }) => {
  const dispatch = useDispatch<any>()
  const currentMediaItem = useSelector(
    (state: any) => state.mediaPlayer.value.currentMediaItem,
  )
  const playerState = useSelector(
    (state: any) => state.mediaPlayer.value.playerState,
  )

  // post_id is the Post ID needed by usePost to fetch songs
  const postId = playlist.post_id || playlist.post?.id
  const { data: postData } = usePost(postId, "songs")
  const playlistDetails = (postData as any)?.post

  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.playlistId === playlist.id
  const isCurrentlyPaused =
    playerState === "paused" && currentMediaItem?.playlistId === playlist.id

  const handlePlay = (e: React.MouseEvent) => {
    e.preventDefault()
    if (isCurrentlyPlaying || isCurrentlyPaused) {
      dispatch(playOrPause())
    } else {
      const songs = sortedSongs(playlistDetails, {
        ...playlist,
        nada_path: playlist.nada_path,
      })
      if (songs.length > 0) dispatch(prependAndPlay(songs))
    }
  }

  const coverImage =
    playlist.post?.external_photo_src ||
    playlist.post?.cover_image_url_small ||
    playlist.post?.cover_image_url ||
    PLAYLIST_PLACEHOLDER_IMAGE_URL

  const byLine = playlist.user_nickname

  return (
    <li className="flex items-center gap-2 py-1">
      <Link to={playlist.nada_path} className="flex-shrink-0">
        <img
          src={coverImage}
          alt={playlist.name}
          style={{ width: 33, height: 33 }}
          className="rounded object-cover"
        />
      </Link>
      <Link to={playlist.nada_path} className="flex-1 min-w-0 leading-tight">
        <div className="text-sm font-medium text-gray-800 truncate">
          {playlist.name}
        </div>
        {byLine && (
          <div className="text-xs text-gray-500 truncate">{byLine}</div>
        )}
      </Link>
      <button
        type="button"
        onClick={handlePlay}
        className="flex-shrink-0 text-green-700 hover:text-green-900 focus:outline-none"
        aria-label={isCurrentlyPlaying ? "Pause" : "Play"}
      >
        {isCurrentlyPlaying ? (
          <PauseCircle size="1.25rem" />
        ) : (
          <PlayCircle size="1.25rem" />
        )}
      </button>
    </li>
  )
}

const PAGE_SIZE = 10

const UserPlaylists: React.FC<UserPlaylistsProps> = ({ userId }) => {
  const [showAll, setShowAll] = useState(false)
  const { data: createdData } = useUserPlaylists(userId)
  const { data: followedData } = useFollowedPlaylists(userId)

  const playlists = useMemo(() => {
    const created: any[] = (createdData as any)?.playlists || []
    const followed: any[] = (followedData as any)?.playlists || []
    const seen = new Set<number>()
    return [...created, ...followed].filter((p) => {
      if (seen.has(p.id)) return false
      seen.add(p.id)
      return true
    })
  }, [createdData, followedData])

  if (playlists.length === 0) return null

  const visible = showAll ? playlists : playlists.slice(0, PAGE_SIZE)
  const hasMore = playlists.length > PAGE_SIZE

  return (
    <>
      <Heading level={2} type="sectionHeader">
        Your Lists
      </Heading>
      <ul>
        {visible.map((playlist) => (
          <PlaylistRow key={playlist.id} playlist={playlist} />
        ))}
      </ul>
      {hasMore && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs text-gray-500 hover:text-gray-700 mt-1 focus:outline-none"
        >
          show more
        </button>
      )}
    </>
  )
}

export default UserPlaylists
